import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Info, Search } from 'lucide-react';
import Calendar from '../../../core/components/dashboard/Calendar/Calendar';
import { getPointService } from '../../../domains/point';
import { getCouponService } from '../../../domains/coupon';
import { usePharmacyCouponHistory } from '../../hooks/coupon/usePharmacyCouponHistory';
import { usePointDistributions } from '../../hooks/point/usePointDistributions';
import { usePagination } from '../../hooks/ui/usePagination';
import { useOutsideClick } from '../../hooks/ui/useOutsideClick';
import useAuthStore from '../../../core/stores/authStore';
import {
  formatAmount,
  formatDateTime,
} from '../shared/dummyData';
import useUIStore from '../../../core/stores/uiStore';
import SummaryCards from '../../../core/components/dashboard/SummaryCards/SummaryCards';
import styles from './EventHistory.module.css';

const EVENT_TYPE_TABS = [
  { id: 'coupon', label: '쿠폰 이벤트' },
  { id: 'point', label: '포인트 이벤트' },
];

const POINT_TYPE_LABELS = {
  EARN: '적립',
  USE: '사용',
  CANCEL: '취소',
  EXPIRE: '만료',
};

const ITEMS_PER_PAGE = 10;

const PERIOD_OPTIONS = [
  { key: 'yesterday', label: '어제' },
  { key: 'lastWeek', label: '지난주' },
  { key: 'lastMonth', label: '지난달' },
  { key: 'thisMonth', label: '이번 달' },
  { key: 'custom', label: '직접입력' },
];

const COUPON_SUB_TABS = [
  { key: 'all', label: '전체' },
  { key: 'FIXED', label: '고정금액' },
  { key: 'PERCENT', label: '비율할인' },
];

const POINT_SUB_TABS = [
  { key: 'all', label: '전체' },
  { key: 'USE', label: '사용' },
  { key: 'CANCEL', label: '취소' },
];

const fmtDate = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDateRangeForPeriod = key => {
  const today = new Date();
  let start, end;
  switch (key) {
    case 'yesterday': {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      start = d;
      end = new Date(d);
      break;
    }
    case 'lastWeek': {
      const d = new Date(today);
      const dayOfWeek = d.getDay();
      end = new Date(d);
      end.setDate(d.getDate() - dayOfWeek - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 6);
      break;
    }
    case 'lastMonth': {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    }
    case 'thisMonth': {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    }
    default: {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    }
  }
  return { start, end };
};

const EventHistory = () => {
  const pharmacy = useAuthStore(state => state.pharmacy);
  const setHeaderTabs = useUIStore(state => state.setHeaderTabs);

  const [eventType, setEventType] = useState('coupon');
  const [searchQuery, setSearchQuery] = useState('');
  const [periodFilter, setPeriodFilter] = useState('thisMonth');
  const [dateRange, setDateRange] = useState(() => getDateRangeForPeriod('thisMonth'));
  const [calendarDraft, setCalendarDraft] = useState(dateRange);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [subFilter, setSubFilter] = useState('all');

  // 쿠폰 사용내역 → hook
  const couponParams = useMemo(() => ({
    start_date: `${dateRange.start.getFullYear()}-${String(dateRange.start.getMonth() + 1).padStart(2, '0')}-${String(dateRange.start.getDate()).padStart(2, '0')}`,
    end_date: `${dateRange.end.getFullYear()}-${String(dateRange.end.getMonth() + 1).padStart(2, '0')}-${String(dateRange.end.getDate()).padStart(2, '0')}`,
    limit: 200,
    offset: 0,
  }), [dateRange]);
  const couponHistoryQuery = usePharmacyCouponHistory(
    pharmacy?.business_registration_number,
    couponParams
  );
  const couponHistory = couponHistoryQuery.data?.items ?? [];
  const isCouponLoading = couponHistoryQuery.isLoading;
  const couponTotal = couponHistoryQuery.data?.total ?? 0;
  const couponSummary = couponHistoryQuery.data?.summary ?? {};

  // 포인트 거래내역 → hook
  const pointParams = useMemo(() => ({
    transaction_type: 'USE',
    start_date: couponParams.start_date,
    end_date: couponParams.end_date,
    limit: 200,
    offset: 0,
  }), [couponParams]);
  const pointQuery = usePointDistributions(
    pharmacy?.business_registration_number,
    pointParams,
    eventType === 'point'
  );
  const pointTransactions = pointQuery.data?.items ?? [];
  const pointTotalCount = pointQuery.data?.total ?? 0;
  const pointLoading = pointQuery.isLoading;

  // Current sub tabs based on eventType
  const currentSubTabs = eventType === 'coupon' ? COUPON_SUB_TABS : POINT_SUB_TABS;

  // Header tabs
  useEffect(() => {
    setHeaderTabs(
      EVENT_TYPE_TABS,
      eventType,
      (id) => setEventType(id)
    );
    return () => setHeaderTabs(null);
  }, [setHeaderTabs, eventType]);

  // Reset page & subFilter
  useEffect(() => {
    setCurrentPage(1);
  }, [eventType, searchQuery, periodFilter, dateRange, subFilter]);

  useEffect(() => {
    setSubFilter('all');
  }, [eventType]);

  // 캘린더 외부 클릭 (hook)
  useOutsideClick(calendarRef, () => setIsCalendarOpen(false), isCalendarOpen);

  // refetch wrapper (mutation 후 호출)
  const fetchCouponHistory = useCallback(() => couponHistoryQuery.refetch(), [couponHistoryQuery]);
  const fetchPointTransactions = useCallback(() => pointQuery.refetch(), [pointQuery]);

  // Filtered coupon data
  const filteredCouponData = useMemo(() => {
    let data = [...couponHistory];

    if (subFilter !== 'all') {
      data = data.filter(d => d.discount_type === subFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      data = data.filter(d =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.barcode || '').includes(q)
      );
    }

    return data;
  }, [couponHistory, searchQuery, subFilter]);

  // Filtered point data (API handles date/type filtering, client-side search only)
  const filteredPointData = useMemo(() => {
    let data = [...pointTransactions];

    if (subFilter !== 'all') {
      data = data.filter(d => d.transaction_type === subFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(d =>
        (d.description || '').toLowerCase().includes(q)
      );
    }

    return data;
  }, [pointTransactions, searchQuery, subFilter]);

  const filteredData = eventType === 'coupon' ? filteredCouponData : filteredPointData;

  // KPI -- summary cards
  const kpiItems = useMemo(() => {
    if (eventType === 'coupon') {
      const total = couponTotal || couponHistory.length;
      const fixedCount = couponHistory.filter(c => c.discount_type === 'FIXED').length;
      const percentCount = couponHistory.filter(c => c.discount_type === 'PERCENT').length;
      return [
        { label: '전체', value: `${total}건` },
        { label: '고정금액', value: `${fixedCount}건` },
        { label: '퍼센트할인', value: `${percentCount}건` },
      ];
    } else {
      const total = pointTransactions.length;
      const useCount = pointTransactions.filter(d => d.transaction_type === 'USE').length;
      const useTotal = pointTransactions
        .filter(d => d.transaction_type === 'USE')
        .reduce((sum, d) => sum + (d.amount || 0), 0);
      return [
        { label: '전체', value: `${pointTotalCount || total}건` },
        // 값(사용 건수·사용 포인트)과 다른 쿠폰용 라벨이 붙어 있던 것 수정 — Figma 에 포인트 탭 KPI 없음, 디자인 확인 필요
        { label: '사용 건수', value: `${useCount}건` },
        { label: '사용 포인트', value: `${formatAmount(useTotal)}P` },
      ];
    }
  }, [eventType, couponHistory, couponTotal, couponSummary, pointTransactions, pointTotalCount]);

  // Pagination (hook)
  const pagination = usePagination({ totalItems: filteredData.length, pageSize: ITEMS_PER_PAGE });
  const { currentPage, setCurrentPage, totalPages } = pagination;
  const paginatedData = pagination.sliceItems(filteredData);

  const handlePeriodChange = key => {
    if (key === 'custom') {
      setPeriodFilter('custom');
      setIsCalendarOpen(true);
      return;
    }
    setPeriodFilter(key);
    setDateRange(getDateRangeForPeriod(key));
  };

  const handleCalendarChange = val => {
    if (val.start && val.end) {
      setPeriodFilter('custom');
      setDateRange({ start: val.start, end: val.end });
      setIsCalendarOpen(false);
    } else if (val.start) {
      setDateRange(prev => ({ ...prev, start: val.start }));
    }
  };

  const SCOPE_LABELS = {
    PHARMACY: '우리 약국',
    PHARM_CODE: '지역 그룹',
    BRANCH_CODE: '브랜치 그룹',
    ALL: '전체',
  };

  const formatCouponAmount = (item) => {
    if (item.discount_type === 'PERCENT') return `${item.amount}%`;
    return `${formatAmount(item.amount || 0)}원`;
  };

  const renderCouponTable = () => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.colCouponId}>쿠폰 ID</th>
          <th className={styles.colTitle}>설명</th>
          <th className={styles.colCenter}>할인 방식</th>
          <th className={styles.colCenter}>금액/비율</th>
          <th className={styles.colCenter}>범위</th>
          <th className={styles.colCenter}>만료일</th>
          <th className={styles.colCenter}>사용/취소</th>
        </tr>
      </thead>
      <tbody>
        {isCouponLoading ? (
          <tr>
            <td colSpan={7} className={styles.emptyState}>
              <div className={styles.emptyText}>조회 중...</div>
            </td>
          </tr>
        ) : paginatedData.length > 0 ? (
          paginatedData.map(item => (
            <tr
              key={item.coupon_id}
              className={styles.clickableRow}
              onClick={() => setSelectedItem({ type: 'coupon', data: item })}
            >
              <td className={styles.couponId}>#{item.coupon_id}</td>
              <td>{item.description || item.title || '-'}</td>
              <td style={{ textAlign: 'center' }}>
                <span className={item.discount_type === 'PERCENT' ? styles.badgePercent : styles.badgeFixed}>
                  {item.discount_type === 'PERCENT' ? '비율할인' : '고정금액'}
                </span>
              </td>
              <td className={styles.amount} style={{ textAlign: 'center' }}>{formatCouponAmount(item)}</td>
              <td style={{ textAlign: 'center' }}>{SCOPE_LABELS[item.scope_type] || item.scope_type || '-'}</td>
              <td style={{ textAlign: 'center' }}>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('ko-KR') : '-'}</td>
              <td style={{ textAlign: 'center' }}>{item.use_count || 0} / {item.cancel_count || 0}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={7} className={styles.emptyState}>
              <div className={styles.emptyText}>쿠폰 내역이 없습니다</div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderPointTable = () => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.colDateTime}>일시</th>
          <th className={styles.colType}>유형</th>
          <th className={styles.colAmount}>금액</th>
          <th className={styles.colDesc}>설명</th>
          <th className={styles.colApproval}>승인번호</th>
        </tr>
      </thead>
      <tbody>
        {pointLoading ? (
          <tr>
            <td colSpan={5} className={styles.emptyState}>
              <div className={styles.emptyText}>조회 중...</div>
            </td>
          </tr>
        ) : paginatedData.length > 0 ? (
          paginatedData.map((item, idx) => (
            <tr
              key={item.id || idx}
              className={styles.clickableRow}
              onClick={() => setSelectedItem({ type: 'point', data: item })}
            >
              <td>{formatDateTime(item.created_at || item.timestamp)}</td>
              <td style={{ textAlign: 'center' }}>
                <span className={styles.badgePoint}>
                  {POINT_TYPE_LABELS[item.transaction_type] || item.transaction_type}
                </span>
              </td>
              <td className={styles.amount} style={{ textAlign: 'center' }}>
                {item.transaction_type === 'USE' || item.transaction_type === 'CANCEL'
                  ? `-${formatAmount(item.amount || 0)}`
                  : `+${formatAmount(item.amount || 0)}`}P
              </td>
              <td style={{ textAlign: 'center' }}>{item.description || '-'}</td>
              <td className={styles.approvalNumber} style={{ textAlign: 'center' }}>{item.approval_number || '-'}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={5} className={styles.emptyState}>
              <div className={styles.emptyText}>해당 조건의 거래내역이 없습니다</div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderCouponModal = (item) => (
    <div className={styles.detailGrid}>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>쿠폰 ID</div>
        <div className={styles.detailValue}>#{item.coupon_id}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>바코드</div>
        <div className={styles.detailValue}>{item.barcode || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>할인 방식</div>
        <div className={styles.detailValue}>{item.discount_type === 'PERCENT' ? '퍼센트' : '고정금액'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>금액/비율</div>
        <div className={styles.detailValue}>{formatCouponAmount(item)}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>사용 범위</div>
        <div className={styles.detailValue}>{SCOPE_LABELS[item.scope_type] || '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>만료일</div>
        <div className={styles.detailValue}>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('ko-KR') : '-'}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>사용 횟수</div>
        <div className={styles.detailValue}>{item.use_count || 0}회</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>취소 횟수</div>
        <div className={styles.detailValue}>{item.cancel_count || 0}회</div>
      </div>
      {item.description && (
        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
          <div className={styles.detailLabel}>설명</div>
          <div className={styles.detailValue}>{item.description}</div>
        </div>
      )}
    </div>
  );

  const renderPointModal = (item) => (
    <div className={styles.detailGrid}>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>거래 유형</div>
        <div className={styles.detailValue}>
          {POINT_TYPE_LABELS[item.transaction_type] || item.transaction_type}
        </div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>금액</div>
        <div className={styles.detailValue}>{formatAmount(item.amount || 0)}P</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>일시</div>
        <div className={styles.detailValue}>{formatDateTime(item.created_at || item.timestamp)}</div>
      </div>
      <div className={styles.detailItem}>
        <div className={styles.detailLabel}>승인번호</div>
        <div className={styles.detailValue}>{item.approval_number || '-'}</div>
      </div>
      {item.description && (
        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
          <div className={styles.detailLabel}>설명</div>
          <div className={styles.detailValue}>{item.description}</div>
        </div>
      )}
      {item.business_number && (
        <div className={`${styles.detailItem} ${styles.fullWidth}`}>
          <div className={styles.detailLabel}>사업자번호</div>
          <div className={styles.detailValue}>{item.business_number}</div>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      {/* Summary Cards Row */}
      <SummaryCards items={kpiItems} />

      {/* Main Table Card */}
      <div className={styles.mainCard}>
        {/* Search Row */}
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrap}>
            <Search className={styles.searchIcon} size={20} />
            <input
              id="event-history-search"
              type="text"
              className={styles.searchInput}
              placeholder={eventType === 'coupon' ? '타이틀, 설명, 바코드 검색' : '설명 검색'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {/* Figma close_small — 검색어 지우기 (입력하면 바로 필터됨) */}
            <button type="button" className={styles.searchClear} aria-label="검색어 지우기" onClick={() => setSearchQuery('')}><X size={16} /></button>
          </div>
          <div className={styles.searchWarning}>
            <Info size={16} />
            키워드 검색 시에는 조회기간에 해당하는 데이터가 검색됩니다. 검색 전 조건을 확인해주세요.
          </div>
        </div>

        {/* Filter Section */}
        <div className={styles.filterSection}>
          {/* 조회기간 */}
          <div className={styles.filterGroup}>
            <div className={styles.filterLabel}>조회기간</div>
            <div className={styles.dateRangeWrapper} ref={isCalendarOpen ? calendarRef : null}>
              <div className={styles.dateInputGroup} onClick={() => { setCalendarDraft(dateRange); setIsCalendarOpen(prev => !prev); }}>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.start)}</span>
                <span className={styles.dateTilde}>~</span>
                <span className={styles.dateDisplay}>{fmtDate(dateRange.end)}</span>
              </div>
              {isCalendarOpen && (
                <div className={styles.calendarDropdown}>
                  <Calendar
                    mode="range"
                    variant="event"
                    value={calendarDraft}
                    onChange={setCalendarDraft}
                    onClear={() => setCalendarDraft({ start: null, end: null })}
                    onClose={() => setIsCalendarOpen(false)}
                    onConfirm={() => {
                      if (calendarDraft?.start && calendarDraft?.end) handleCalendarChange(calendarDraft);
                    }}
                  />
                </div>
              )}
            </div>
            <div className={styles.pillGroup}>
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  className={`${styles.pill} ${periodFilter === opt.key ? styles.pillActive : ''}`}
                  onClick={() => handlePeriodChange(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className={styles.filterDivider} />

          {/* 유형 */}
          <div className={styles.filterGroup}>
            <div className={styles.filterLabel}>유형</div>
            <div className={styles.pillGroup}>
              {currentSubTabs.map(tab => (
                <button
                  key={tab.key}
                  className={`${styles.pill} ${subFilter === tab.key ? styles.pillActive : ''}`}
                  onClick={() => setSubFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableBox}>
          {eventType === 'coupon' ? renderCouponTable() : renderPointTable()}
        </div>

        {/* Pagination */}
        <div className={styles.paginationBar}>
          <div className={styles.paginationInfo}>
            검색결과 {filteredData.length}개
          </div>
          {filteredData.length > 0 && (
            <div className={styles.paginationButtons}>
              <button
                className={styles.pageBtn}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                &#8249;
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`${styles.pageBtn} ${currentPage === page ? styles.activePage : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className={styles.pageBtn}
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                &#8250;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div className={styles.modalOverlay} onClick={() => setSelectedItem(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{selectedItem.type === 'coupon' ? '쿠폰 사용내역 상세' : '포인트 거래 상세'}</h2>
              <button className={styles.closeButton} onClick={() => setSelectedItem(null)}>
                <X size={20} />
              </button>
            </div>
            {selectedItem.type === 'coupon'
              ? renderCouponModal(selectedItem.data)
              : renderPointModal(selectedItem.data)
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default EventHistory;
